import { createSlice } from '@reduxjs/toolkit'

/**
 * Canonical key for `byProjectId` — always string so the same project never
 * splits across two buckets (e.g. number vs string from the route).
 * @param {unknown} projectId
 * @returns {string}
 */
export const normalizeNtierProjectDetailsFilterProjectId = (projectId) => {
  if (projectId == null) return ''
  return String(projectId).trim()
}

/** @param {unknown} v @returns {string[]} */
export const normalizeNtierMultiFilterValues = (v) => {
  if (v == null) return []
  if (Array.isArray(v)) {
    return [...new Set(v.map((x) => String(x ?? '').trim()).filter(Boolean))]
  }
  const s = String(v).trim()
  if (!s) return []
  return [s]
}

export const NTIER_PROJECT_DETAILS_DEFAULT_FILTERS = Object.freeze({
  supplier: [],
  country: [],
  /** All false = no confidence filter (show all tiers). */
  confidenceTierMask: Object.freeze({ low: false, med: false, high: false }),
  /** Applied LTM range (dollars). `ltmMax: null` means “up to current grid max” until next Apply. */
  ltmMin: 0,
  ltmMax: null,
})

const initialState = {
  /** @type {Record<string, { supplier: string[], country: string[], confidenceTierMask: object, ltmMin: number, ltmMax: number|null }>} */
  byProjectId: {},
}

export const ntierProjectDetailsFiltersSlice = createSlice({
  name: 'ntierProjectDetailsFilters',
  initialState,
  reducers: {
    setNtierProjectDetailsAppliedFilters: (state, action) => {
      const { projectId, supplier, country, confidenceTierMask, ltmMin, ltmMax } = action.payload || {}
      const id = normalizeNtierProjectDetailsFilterProjectId(projectId)
      if (!id) return
      const prev = state.byProjectId[id] || NTIER_PROJECT_DETAILS_DEFAULT_FILTERS
      const maskIn = confidenceTierMask || prev.confidenceTierMask || NTIER_PROJECT_DETAILS_DEFAULT_FILTERS.confidenceTierMask
      state.byProjectId[id] = {
        supplier: supplier !== undefined ? normalizeNtierMultiFilterValues(supplier) : normalizeNtierMultiFilterValues(prev.supplier),
        country: country !== undefined ? normalizeNtierMultiFilterValues(country) : normalizeNtierMultiFilterValues(prev.country),
        confidenceTierMask: {
          low: !!maskIn.low,
          med: !!maskIn.med,
          high: !!maskIn.high,
        },
        ltmMin: ltmMin !== undefined && ltmMin !== null ? Number(ltmMin) || 0 : prev.ltmMin ?? 0,
        ltmMax: ltmMax !== undefined ? (ltmMax === null ? null : Number(ltmMax)) : prev.ltmMax ?? null,
      }
    },
    resetNtierProjectDetailsAppliedFilters: (state, action) => {
      const id = normalizeNtierProjectDetailsFilterProjectId(action.payload?.projectId)
      if (!id) return
      state.byProjectId[id] = {
        supplier: [],
        country: [],
        confidenceTierMask: { low: false, med: false, high: false },
        ltmMin: 0,
        ltmMax: null,
      }
    },
  },
})

export const { setNtierProjectDetailsAppliedFilters, resetNtierProjectDetailsAppliedFilters } =
  ntierProjectDetailsFiltersSlice.actions

/** @param {unknown} state @param {string} [projectId] */
export const selectNtierProjectDetailsAppliedFilters = (state, projectId) => {
  const id = normalizeNtierProjectDetailsFilterProjectId(projectId)
  if (!id) return NTIER_PROJECT_DETAILS_DEFAULT_FILTERS
  const raw = state.ntierProjectDetailsFilters?.byProjectId?.[id]
  if (!raw) return NTIER_PROJECT_DETAILS_DEFAULT_FILTERS
  return {
    ...raw,
    supplier: normalizeNtierMultiFilterValues(raw.supplier),
    country: normalizeNtierMultiFilterValues(raw.country),
  }
}

export default ntierProjectDetailsFiltersSlice.reducer
